import React, { useRef } from 'react';
import { Controller } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import VECIErrorMessage from './VECIErrorMessage';
import { toTitleCase } from '../../utils/textFormat';

const formatIDR = (val) => {
  if (val === undefined || val === null || val === '') return '';
  return new Intl.NumberFormat('id-ID').format(val);
};

const parseNumber = (val) => {
  return val.replace(/\D/g, '');
};

// Sektor Bisnis UTAMA — daftar kategori besar yang ringkas (kembali ke daftar awal).
const mainSectorOptions = [
  { value: 'Pertanian', label: 'Pertanian' },
  { value: 'Industri Pengolahan', label: 'Industri Pengolahan' },
  { value: 'Jasa', label: 'Jasa' },
  { value: 'Perdagangan / Retail', label: 'Perdagangan / Retail' },
  { value: 'Pariwisata', label: 'Pariwisata' },
];

// Sektor Bisnis LAINNYA — daftar rincian unit usaha BUMDes (hasil riset:
// LKM/simpan pinjam, air bersih/PAMDes, persampahan, energi terbarukan desa,
// layanan digital desa, dll). Sengaja berbeda dari daftar sektor utama.
const secondarySectorOptions = [
  { value: 'Perkebunan', label: 'Perkebunan' },
  { value: 'Peternakan', label: 'Peternakan' },
  { value: 'Perikanan', label: 'Perikanan' },
  {
    value: 'Jasa Keuangan (Simpan Pinjam / LKM)',
    label: 'Jasa Keuangan (Simpan Pinjam / LKM)',
  },
  {
    value: 'Pengelolaan Air Bersih (PAMDes)',
    label: 'Pengelolaan Air Bersih (PAMDes)',
  },
  {
    value: 'Pengelolaan Sampah & Lingkungan',
    label: 'Pengelolaan Sampah & Lingkungan',
  },
  { value: 'Energi Terbarukan Desa', label: 'Energi Terbarukan Desa' },
  {
    value: 'Persewaan Alat & Konstruksi',
    label: 'Persewaan Alat & Konstruksi',
  },
  {
    value: 'Layanan Digital & Teknologi Desa',
    label: 'Layanan Digital & Teknologi Desa',
  },
  { value: 'Lainnya', label: 'Lainnya' },
];

const sectorExampleHints = {
  Pertanian:
    'Contoh produk/jasa: padi, sayur mayur, sewa alsintan, penggilingan padi, dll.',
  'Industri Pengolahan':
    'Contoh: keripik, abon, kerupuk, pupuk organik, pengemasan & branding produk lokal.',
  Jasa: 'Contoh: simpan pinjam, air bersih, persewaan alat, kebersihan/pengelolaan sampah, transportasi desa.',
  'Perdagangan / Retail':
    'Contoh: toko sembako/kelontong (waserda), distribusi pupuk & saprotan, agen kebutuhan desa.',
  Pariwisata:
    'Contoh: objek wisata desa, homestay, kuliner khas, paket agrowisata/edukasi.',
  Perkebunan: 'Contoh: kopi, kelapa, karet, sawit rakyat, hasil kebun lainnya.',
  Peternakan:
    'Contoh: penggemukan sapi/kambing, ayam petelur, pakan ternak, kandang komunal.',
  Perikanan:
    'Contoh: budidaya lele/nila, keramba, pengolahan ikan, penjualan benih ikan.',
  'Jasa Keuangan (Simpan Pinjam / LKM)':
    'Contoh: unit simpan pinjam, lembaga keuangan mikro desa, pembiayaan modal usaha warga.',
  'Pengelolaan Air Bersih (PAMDes)':
    'Contoh: penyediaan & distribusi air bersih, iuran air rumah tangga desa.',
  'Pengelolaan Sampah & Lingkungan':
    'Contoh: bank sampah, pengangkutan & pengolahan sampah, kompos/daur ulang.',
  'Energi Terbarukan Desa':
    'Contoh: PLTMH, biogas, PLTS komunal, penjualan listrik desa.',
  'Persewaan Alat & Konstruksi':
    'Contoh: sewa alat berat, molen, scaffolding, material bangunan, jasa konstruksi kecil.',
  'Layanan Digital & Teknologi Desa':
    'Contoh: internet desa (RT/RW-net), marketplace produk desa, layanan administrasi digital.',
  Lainnya:
    'Sebutkan produk atau jasa spesifik yang sesuai dengan sektor bisnis BUMDes Anda.',
};

const selectStyles = (isError) => ({
  control: (base) => ({
    ...base,
    borderRadius: '0.5rem',
    borderColor: isError ? '#dc3545' : '#dee2e6',
    borderWidth: '2px',
    padding: '2px',
    '&:hover': {
      borderColor: isError ? '#dc3545' : '#0d6efd',
    },
  }),
  // Pastikan menu dropdown selalu di atas elemen lain (mis. tombol hapus
  // pada input produk/jasa di bawahnya) supaya tidak tembus/terlihat.
  menu: (base) => ({ ...base, zIndex: 5 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

const VECIInfoBumdes = ({
  register,
  errors,
  control,
  setValue,
  fields,
  append,
  remove,
  secondaryFields,
  secondaryAppend,
  secondaryRemove,
}) => {
  // Saat sektor utama dipilih "Lainnya", fokus otomatis digulir ke blok
  // "Bisnis Lainnya" di bawah agar user langsung merinci sektor spesifiknya.
  const secondaryBlockRef = useRef(null);

  return (
    <div className="card border-0 shadow-sm rounded-4 p-4 p-md-5">
      <div className="d-flex align-items-center mb-4 border-bottom pb-3">
        <div className="bg-primary bg-opacity-10 p-3 rounded-4 me-3">
          <i className="bi bi-building text-primary fs-3"></i>
        </div>
        <div>
          <h4 className="fw-bold mb-0">Informasi BUMDes</h4>
          <p className="text-muted small mb-0">
            Kondisi terkini operasional & finansial
          </p>
        </div>
      </div>

      {/* Penjelasan hubungan Sektor (lingkar luar) vs Produk/Jasa (lingkar dalam) */}
      <div className="alert bg-primary bg-opacity-10 border-0 rounded-4 d-flex gap-3 mb-4">
        <i className="bi bi-diagram-3 text-primary fs-4 flex-shrink-0"></i>
        <div className="small text-dark">
          <span className="fw-bold">Sektor</span> adalah{' '}
          <span className="fw-semibold">kategori besar (lingkaran luar)</span>{' '}
          bidang usaha BUMDes, sedangkan{' '}
          <span className="fw-bold">Produk/Jasa</span> adalah{' '}
          <span className="fw-semibold">
            item konkret di dalam sektor itu (lingkaran dalam)
          </span>
          . Contoh: sektor <em>Pertanian</em> &rarr; produknya bisa{' '}
          <em>beras, sewa traktor, penggilingan padi</em>. Jadi keduanya saling
          berkaitan — bukan dua hal terpisah.
        </div>
      </div>

      {/* ===================== BLOK BISNIS UTAMA ===================== */}
      <div className="border border-2 border-primary border-opacity-25 rounded-4 p-3 p-md-4 mb-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="badge bg-primary rounded-pill">Bisnis Utama</span>
          <span className="text-muted small">Wajib diisi</span>
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Sektor Bisnis Utama</label>
          <Controller
            name="businessInfo.mainSector"
            control={control}
            rules={{ required: 'Pilih atau isi sektor bisnis utama BUMDes' }}
            render={({ field }) => (
              <>
                <CreatableSelect
                  {...field}
                  isClearable
                  options={mainSectorOptions}
                  value={
                    field.value
                      ? { value: field.value, label: field.value }
                      : null
                  }
                  onChange={(val) => {
                    const nextValue = val ? toTitleCase(val.value) : '';
                    field.onChange(nextValue);
                    // Pilih "Lainnya" → gulir otomatis ke blok Bisnis Lainnya
                    // supaya user langsung merinci sektor spesifiknya di sana.
                    if (nextValue === 'Lainnya' && secondaryBlockRef.current) {
                      secondaryBlockRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                      });
                    }
                  }}
                  placeholder="Pilih atau ketik sektor utama..."
                  classNamePrefix="react-select"
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                  styles={selectStyles(!!errors?.businessInfo?.mainSector)}
                />
                {field.value && sectorExampleHints[field.value] && (
                  <div className="form-text text-muted small mt-1">
                    <i className="bi bi-info-circle me-1"></i>
                    {sectorExampleHints[field.value]}
                  </div>
                )}
                <VECIErrorMessage error={errors?.businessInfo?.mainSector} />
              </>
            )}
          />
        </div>

        <div>
          <label className="form-label fw-semibold mb-1">
            Produk/Jasa Utama{' '}
            <span className="text-muted fw-normal">(Maks 5)</span>
          </label>
          <p className="text-muted small mb-2">
            Sebutkan produk/jasa nyata yang dijual di sektor utama di atas.
          </p>
          <div className="row g-2">
            {fields.length === 0 ? (
              <div className="col-12">
                <div className="input-group">
                  <span className="input-group-text bg-light border-2 border-end-0 text-muted small">
                    1
                  </span>
                  <input
                    disabled
                    className="form-control border-2 bg-light"
                    placeholder="Masukkan nama produk atau jasa..."
                  />
                </div>
              </div>
            ) : (
              fields.map((field, index) => (
                <div key={field.id} className="col-12">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-2 border-end-0 text-muted small">
                      {index + 1}
                    </span>
                    <input
                      {...register(`businessInfo.productsServices.${index}`, {
                        required:
                          'Harap isi nama produk/jasa atau hapus baris ini',
                        onBlur: (e) =>
                          setValue(
                            `businessInfo.productsServices.${index}`,
                            toTitleCase(e.target.value),
                          ),
                      })}
                      className={`form-control border-2 ${errors?.businessInfo?.productsServices?.[index] ? 'is-invalid border-danger' : ''}`}
                      placeholder="Masukkan nama produk atau jasa..."
                    />
                    {fields.length > 1 && (
                      <button
                        className="btn btn-outline-danger border-2"
                        type="button"
                        onClick={() => remove(index)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    )}
                  </div>
                  <VECIErrorMessage
                    error={errors?.businessInfo?.productsServices?.[index]}
                  />
                </div>
              ))
            )}
          </div>
          {fields.length < 5 && (
            <button
              type="button"
              className="btn btn-outline-primary w-100 rounded-3 border-2 border-dashed mt-2 d-flex align-items-center justify-content-center gap-2"
              onClick={() => append('')}
            >
              <i className="bi bi-plus-lg"></i> Tambah Produk/Jasa
            </button>
          )}
        </div>
      </div>

      {/* ===================== BLOK BISNIS LAINNYA ===================== */}
      <div
        ref={secondaryBlockRef}
        className="border border-2 border-light-subtle rounded-4 p-3 p-md-4 mb-4 bg-light bg-opacity-50"
      >
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className="badge bg-secondary rounded-pill">
            Bisnis Lainnya
          </span>
          <span className="text-muted small">Opsional</span>
        </div>
        <p className="text-muted small mb-3">
          Isi bagian ini <span className="fw-semibold">hanya jika</span> BUMDes
          Anda punya sektor usaha{' '}
          <span className="fw-semibold">kedua yang berbeda</span> dari sektor
          utama. Kalau tidak ada, boleh dikosongkan.
        </p>

        <div className="mb-3">
          <label className="form-label fw-semibold">
            Sektor Bisnis Lainnya{' '}
            <span className="text-muted fw-normal">(Opsional)</span>
          </label>
          <Controller
            name="businessInfo.secondarySector"
            control={control}
            render={({ field }) => (
              <>
                <CreatableSelect
                  {...field}
                  isClearable
                  options={secondarySectorOptions}
                  value={
                    field.value
                      ? { value: field.value, label: field.value }
                      : null
                  }
                  onChange={(val) =>
                    field.onChange(val ? toTitleCase(val.value) : '')
                  }
                  placeholder="Pilih atau ketik sektor kedua..."
                  classNamePrefix="react-select"
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                  styles={selectStyles(false)}
                />
                {field.value && sectorExampleHints[field.value] && (
                  <div className="form-text text-muted small mt-1">
                    <i className="bi bi-info-circle me-1"></i>
                    {sectorExampleHints[field.value]}
                  </div>
                )}
              </>
            )}
          />
        </div>

        <div>
          <label className="form-label fw-semibold mb-1">
            Produk/Jasa Lainnya{' '}
            <span className="text-muted fw-normal">(Maks 5)</span>
          </label>
          <p className="text-muted small mb-2">
            Produk/jasa dari <span className="fw-semibold">sektor lainnya</span>{' '}
            di atas — terpisah dari Produk/Jasa Utama.
          </p>
          <div className="row g-2">
            {secondaryFields.length === 0 ? (
              <div className="col-12">
                <div className="text-muted small fst-italic">
                  Belum ada. Klik &ldquo;Tambah&rdquo; jika ada produk/jasa dari
                  sektor lainnya.
                </div>
              </div>
            ) : (
              secondaryFields.map((field, index) => (
                <div key={field.id} className="col-12">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-2 border-end-0 text-muted small">
                      {index + 1}
                    </span>
                    <input
                      {...register(
                        `businessInfo.secondaryProductsServices.${index}`,
                        {
                          onBlur: (e) =>
                            setValue(
                              `businessInfo.secondaryProductsServices.${index}`,
                              toTitleCase(e.target.value),
                            ),
                        },
                      )}
                      className="form-control border-2"
                      placeholder="Masukkan nama produk atau jasa lainnya..."
                    />
                    <button
                      className="btn btn-outline-danger border-2"
                      type="button"
                      onClick={() => secondaryRemove(index)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          {secondaryFields.length < 5 && (
            <button
              type="button"
              className="btn btn-outline-secondary w-100 rounded-3 border-2 border-dashed mt-2 d-flex align-items-center justify-content-center gap-2"
              onClick={() => secondaryAppend('')}
            >
              <i className="bi bi-plus-lg"></i> Tambah Produk/Jasa Lainnya
            </button>
          )}
        </div>
      </div>

      {/* ===================== BLOK KEUANGAN ===================== */}
      <div className="border border-2 border-light-subtle rounded-4 p-3 p-md-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="badge bg-success rounded-pill">Keuangan BUMDes</span>
        </div>
        <div className="row g-4">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Modal Awal</label>
            <Controller
              name="businessInfo.initialCapital"
              control={control}
              rules={{ required: 'Isi modal awal berdirinya BUMDes' }}
              render={({ field }) => (
                <div className="input-group">
                  <span className="input-group-text bg-light border-2 border-end-0 text-muted px-3">
                    Rp
                  </span>
                  <input
                    type="text"
                    className={`form-control border-2 ${errors?.businessInfo?.initialCapital ? 'is-invalid border-danger' : ''}`}
                    placeholder="0"
                    value={formatIDR(field.value)}
                    onChange={(e) =>
                      field.onChange(parseNumber(e.target.value))
                    }
                  />
                </div>
              )}
            />
            <div className="form-text text-muted small">
              Modal awal saat BUMDes pertama kali didirikan.
            </div>
            <VECIErrorMessage error={errors?.businessInfo?.initialCapital} />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">Modal Saat Ini</label>
            <Controller
              name="businessInfo.currentCapital"
              control={control}
              rules={{ required: 'Isi estimasi modal/aset BUMDes per bulan' }}
              render={({ field }) => (
                <div className="input-group">
                  <span className="input-group-text bg-light border-2 border-end-0 text-muted px-3">
                    Rp
                  </span>
                  <input
                    type="text"
                    className={`form-control border-2 ${errors?.businessInfo?.currentCapital ? 'is-invalid border-danger' : ''}`}
                    placeholder="0"
                    value={formatIDR(field.value)}
                    onChange={(e) =>
                      field.onChange(parseNumber(e.target.value))
                    }
                  />
                </div>
              )}
            />
            <div className="form-text text-muted small">
              Estimasi modal/aset yang dikelola BUMDes secara rutin per bulan.
            </div>
            <VECIErrorMessage error={errors?.businessInfo?.currentCapital} />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">
              Pendapatan Kotor Rata-rata per Bulan
            </label>
            <Controller
              name="businessInfo.monthlyGrossIncome"
              control={control}
              rules={{
                required:
                  'Isi pendapatan kotor per bulan (tulis 0 jika belum ada)',
              }}
              render={({ field }) => (
                <div className="input-group">
                  <span className="input-group-text bg-light border-2 border-end-0 text-muted px-3">
                    Rp
                  </span>
                  <input
                    type="text"
                    className={`form-control border-2 ${errors?.businessInfo?.monthlyGrossIncome ? 'is-invalid border-danger' : ''}`}
                    placeholder="0"
                    value={formatIDR(field.value)}
                    onChange={(e) =>
                      field.onChange(parseNumber(e.target.value))
                    }
                  />
                </div>
              )}
            />
            <div className="form-text text-muted small">
              Rata-rata omzet/pendapatan kotor BUMDes per bulan.
            </div>
            <VECIErrorMessage
              error={errors?.businessInfo?.monthlyGrossIncome}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">
              Pendapatan Kotor dalam Satu Tahun Terakhir
            </label>
            <Controller
              name="businessInfo.annualGrossIncome"
              control={control}
              rules={{
                required:
                  'Isi pendapatan kotor per tahun (tulis 0 jika belum ada)',
              }}
              render={({ field }) => (
                <div className="input-group">
                  <span className="input-group-text bg-light border-2 border-end-0 text-muted px-3">
                    Rp
                  </span>
                  <input
                    type="text"
                    className={`form-control border-2 ${errors?.businessInfo?.annualGrossIncome ? 'is-invalid border-danger' : ''}`}
                    placeholder="0"
                    value={formatIDR(field.value)}
                    onChange={(e) =>
                      field.onChange(parseNumber(e.target.value))
                    }
                  />
                </div>
              )}
            />
            <div className="form-text text-muted small">
              Total pendapatan kotor dalam satu tahun terakhir.
            </div>
            <VECIErrorMessage error={errors?.businessInfo?.annualGrossIncome} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VECIInfoBumdes;
